<template>
    <span>
        <textarea
            ref="textarea"
            v-model="localValue"
            class="valueinput"
            wrap="soft"
            rows="1"
            style="resize: both; overflow-y: hidden;"
            :placeholder="$t('lang.main.f_rules_new_str')"
            rel="input"
            @input="onInput"
            @mousedown="onResizeStart"
            @mouseup="onResizeEnd"
            @blur="onBlur"
        ></textarea>
    </span>
</template>

<script>
import { Listeners } from "keysso/constants/listeners";

export default {
    props: {
        item: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            resizeRafId: null,
            maxAutoHeight: 0,
            commitTimerId: null,
            localValue: this.item?.value || "",
            isManualResized: false,
            isMaxHeightReached: false,
            resizeStartWidth: 0,
            resizeStartHeight: 0,
            lineBreaksCount: ((this.item?.value || "").match(/\n/g) || []).length
        };
    },
    mounted() {
        this.initAutoResizeMetrics();
        this.$nextTick(() => {
            this.scheduleAutoResize();
        });
    },
    beforeUnmount() {
        if (this.resizeRafId !== null) {
            cancelAnimationFrame(this.resizeRafId);
            this.resizeRafId = null;
        }

        if (this.commitTimerId !== null) {
            clearTimeout(this.commitTimerId);
            this.commitTimerId = null;
        }
    },
    methods: {
        submitFormFilter() {
            this.$emitter.emit(Listeners.KS_FILTER_SUBMIT_FORM);
        },
        initAutoResizeMetrics() {
            const el = this.$refs.textarea;
            if (!el) return;

            const style = getComputedStyle(el);
            const fontSize = parseFloat(style.fontSize);
            let lineHeight = parseFloat(style.lineHeight);

            if (lineHeight < 4) {
                lineHeight = fontSize * lineHeight;
            }

            const padding =
                parseFloat(style.paddingTop) +
                parseFloat(style.paddingBottom);

            const border =
                parseFloat(style.borderTopWidth) +
                parseFloat(style.borderBottomWidth);

            const maxRows = 5;
            this.maxAutoHeight = lineHeight * maxRows + padding + border + 2;
        },
        scheduleAutoResize() {
            if (this.isManualResized || this.isMaxHeightReached) return;
            if (this.resizeRafId !== null) return;

            this.resizeRafId = requestAnimationFrame(() => {
                this.resizeRafId = null;
                this.autoResize();
            });
        },
        scheduleCommit() {
            if (this.commitTimerId !== null) {
                clearTimeout(this.commitTimerId);
            }

            this.commitTimerId = setTimeout(() => {
                this.commitTimerId = null;
                this.commitValue();
            }, 120);
        },
        commitValue() {
            if (this.item.value !== this.localValue) {
                this.item.value = this.localValue;
            }
        },
        autoResize() {
            if (this.isManualResized || this.isMaxHeightReached) return;
            const el = this.$refs.textarea;
            if (!el) return;

            if (!this.maxAutoHeight) {
                this.initAutoResizeMetrics();
            }

            el.style.height = "auto";

            if (el.scrollHeight > this.maxAutoHeight) {
                el.style.height = this.maxAutoHeight + "px";
                el.style.overflowY = "auto";
                this.isMaxHeightReached = true;
            } else {
                el.style.height = (el.scrollHeight + 2) + "px";
                el.style.overflowY = "hidden";
            }
        },
        onInput() {
            const nextLineBreaksCount = (this.localValue.match(/\n/g) || []).length;
            if (nextLineBreaksCount !== this.lineBreaksCount) {
                this.lineBreaksCount = nextLineBreaksCount;
                this.scheduleAutoResize();
            }
            this.scheduleCommit();
        },
        onResizeStart() {
            const el = this.$refs.textarea;
            if (!el) return;
            this.resizeStartWidth = el.offsetWidth;
            this.resizeStartHeight = el.offsetHeight;
        },
        onResizeEnd() {
            const el = this.$refs.textarea;
            if (!el) return;

            if (
                el.offsetWidth !== this.resizeStartWidth ||
                el.offsetHeight !== this.resizeStartHeight
            ) {
                this.isManualResized = true;
            }
        },
        onBlur() {
            if (this.commitTimerId !== null) {
                clearTimeout(this.commitTimerId);
                this.commitTimerId = null;
            }

            this.commitValue();
            this.lineBreaksCount = (this.localValue.match(/\n/g) || []).length;

            if (!this.isManualResized && this.isMaxHeightReached) {
                this.isMaxHeightReached = false;
                this.scheduleAutoResize();
            }
        }
    },
    watch: {
        "item.value"(newValue) {
            if (newValue !== this.localValue) {
                this.localValue = newValue || "";
                this.lineBreaksCount = (this.localValue.match(/\n/g) || []).length;
                if (!this.isManualResized && !this.isMaxHeightReached) {
                    this.$nextTick(() => {
                        this.scheduleAutoResize();
                    });
                }
            }
        }
    },
};
</script>

<style scoped>
textarea {
    font-size: 13px;
    line-height: 15px;
    overflow: hidden;
    width: 230px;
}
</style>
